import { useState, useMemo, useContext } from 'react';
import { useComments } from 'app/api/useComments';
import { useEditorSelector } from 'app/state/contexts/EditorContext';
import { i18n } from 'app/utils/i18n';
import config from 'config';
import moment from 'moment';
import 'moment/locale/sv';
import { SidebarContext } from 'app/state/contexts/SidebarContext';
import { useHistory } from 'react-router-dom';
const CommentSelection = {
  ALL: { value: 'all', label: 'All' },
  DOCUMENT: { value: 'document', label: 'Document' },
  SECTION: { value: 'section', label: 'Section' },
};

export const NotificationsDrawer = ({ onClose }) => {
  const { superId: sectionSuperId } = useEditorSelector((editor) => editor.section);
  const { superId: documentSuperId } = useEditorSelector((editor) => editor.pDocument);
  const history = useHistory();
  const { commentNotifications, isCommentNotificationsLoading } = useComments(sectionSuperId);
  const [commentSelection, setCommentSelection] = useState(CommentSelection.ALL);
  const sidebarContext = useContext(SidebarContext);
  const setActiveComment = sidebarContext?.setActiveComment;
  const setIsSidebarOpen = sidebarContext?.setIsSidebarOpen;
  const filteredNotifications = useMemo(() => {
    switch (commentSelection.value) {
      case CommentSelection.ALL.value:
        return commentNotifications;
      case CommentSelection.DOCUMENT.value:
        return commentNotifications?.filter((c) => c.meta.documentSuperId === documentSuperId);
      case CommentSelection.SECTION.value:
        return commentNotifications?.filter((c) => c.meta.sectionSuperId === sectionSuperId);
      default:
        return commentNotifications;
    }
  }, [commentNotifications, commentSelection.value, documentSuperId, sectionSuperId]);

  const closedComments = useMemo(() => filteredNotifications?.filter((c) => c.closedAt !== null), [filteredNotifications]);
  const openComments = useMemo(() => filteredNotifications?.filter((c) => c.closedAt === null), [filteredNotifications]);

  const handleNotificationClick = (notification) => {
    const { meta, ...comment } = notification;
    if (notification.meta.documentSuperId === documentSuperId && notification.sectionSuperId === sectionSuperId) {
      onClose();
      setIsSidebarOpen(5);
      setActiveComment(comment);
    } else {
      history.push(`/documents/${notification.meta.documentSuperId}/sections/${notification.sectionSuperId}`);
      onClose();
      setIsSidebarOpen(5);
      setActiveComment(comment);
    }
  };

  const renderFilterButtons = () => {
    const isDocumentDisabled = !documentSuperId;
    const isSectionDisabled = !sectionSuperId;

    return (
      <>
        <button
          disabled={false}
          className={`drawer-filter-button ${commentSelection.value === CommentSelection.ALL.value && 'active'}`}
          onClick={() => setCommentSelection(CommentSelection.ALL)}
        >
          {CommentSelection.ALL.label}
        </button>
        <button
          disabled={isDocumentDisabled}
          className={`drawer-filter-button ${commentSelection.value === CommentSelection.DOCUMENT.value && 'active'}`}
          onClick={() => !isDocumentDisabled && setCommentSelection(CommentSelection.DOCUMENT)}
        >
          {CommentSelection.DOCUMENT.label}
        </button>
        <button
          disabled={isSectionDisabled}
          className={`drawer-filter-button ${commentSelection.value === CommentSelection.SECTION.value && 'active'}`}
          onClick={() => !isSectionDisabled && setCommentSelection(CommentSelection.SECTION)}
        >
          {CommentSelection.SECTION.label}
        </button>
      </>
    );
  };

  return (
    <div className="drawer-content">
      <div className="drawer-controls columns col-1fr-auto" onClick={onClose}>
        <i className="fa-regular fa-arrow-right-long drawer-arrow " />
      </div>
      <p className="drawer-title">{i18n('notifications.title')}</p>
      <div className="divider" />
      <div className="drawer-filters">{renderFilterButtons()}</div>

      {!isCommentNotificationsLoading && (
        <div className="notification-list">
          {openComments?.length > 0 && (
            <>
              <span>
                <b>{i18n('common.open')}</b>
                {` (${openComments?.length})`}
              </span>
              {openComments?.map((notification) => (
                <NotificationItem key={notification.id} notification={notification} locale={config.defaultLocale || 'sv'} onClick={handleNotificationClick} />
              ))}
            </>
          )}
          {closedComments?.length > 0 && (
            <>
              <span>
                <b>{i18n('common.closed')}</b>
                {` (${closedComments?.length})`}
              </span>
              {closedComments?.map((notification) => (
                <NotificationItem key={notification.id} notification={notification} locale={config.defaultLocale || 'sv'} onClick={handleNotificationClick} />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const formatDate = (date, locale) => {
  moment.locale(locale);
  return moment(new Date(date)).fromNow();
};

const NotificationItem = ({ notification, locale, onClick }) => {
  return (
    <div key={notification.id} className="notification-item-wrapper" onClick={() => onClick(notification)}>
      <div className="user--circle">
        {notification.meta.author
          .split(' ')
          .map((w) => w.substr(0, 1))
          .map((c) => c.toUpperCase())
          .join('')}
      </div>
      <div>
        <span className="notification-item-text">
          <b>{`${notification.meta.author} `}</b>
          <span>
            {i18n('notifications.mentioned-text')} {notification.message}
          </span>
        </span>
        <div className="notification-item-info">
          <span>{formatDate(notification.createdAt, locale)} </span>
          <span className="notification-item-meta">{`${notification.meta.organizationName}, ${notification.meta.documentName}, ${notification.meta.sectionName}`}</span>
        </div>
      </div>
    </div>
  );
};
